import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Loader2, AlertCircle, X } from 'lucide-react'
import { loginRequestSchema, type LoginFormInput, type LoginRequest, type OAuthProvider } from '@/types/auth'
import { Button, Input, Label, Checkbox, Alert, AlertDescription } from '@/components/ui'
import { cn } from '@/lib/utils'
import { authApi, getIntendedDestination } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/auth-store'
import { isApiError } from '@/lib/api-client'
import GhostCursor from '@/components/GhostCursor/GhostCursor'

/**
 * OAuth provider configuration
 * These providers are displayed but not yet functional
 */
const oauthProviders: Array<{
  id: OAuthProvider
  name: string
  icon: React.ReactNode
  bgColor: string
  textColor: string
}> = [
  {
    id: 'azure_ad',
    name: 'Microsoft',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 21 21" fill="currentColor">
        <path d="M0 0h10v10H0zM11 0h10v10H11zM0 11h10v10H0zM11 11h10v10H11z" />
      </svg>
    ),
    bgColor: 'bg-[#2f2f2f] hover:bg-[#1f1f1f]',
    textColor: 'text-white',
  },
  {
    id: 'auth0',
    name: 'Auth0',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21.98 7.448L19.62 0H4.347L2.02 7.448c-1.352 4.312.03 9.206 3.815 12.015L12.007 24l6.157-4.552c3.755-2.81 5.182-7.688 3.815-12.015l-6.16 4.58 2.343 7.45-6.157-4.597-6.158 4.58 2.358-7.433-6.188-4.55 7.63-.045L12.008 0l2.356 7.404 7.615.044z" />
      </svg>
    ),
    bgColor: 'bg-[#eb5424] hover:bg-[#d14a1f]',
    textColor: 'text-white',
  },
  {
    id: 'okta',
    name: 'Okta',
    icon: (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.389 0 0 5.35 0 12s5.35 12 12 12 12-5.35 12-12S18.611 0 12 0zm0 18c-3.325 0-6-2.675-6-6s2.675-6 6-6 6 2.675 6 6-2.675 6-6 6z" />
      </svg>
    ),
    bgColor: 'bg-[#007dc1] hover:bg-[#006aa3]',
    textColor: 'text-white',
  },
]

/**
 * Login page component
 * Implements split-screen layout following shadcn authentication example
 * Redirects authenticated users to launchpad
 */
export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const login = useAuthStore((state) => state.login)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired)

  // Redirect to launchpad if user is already authenticated with valid token
  useEffect(() => {
    if (isAuthenticated && !isTokenExpired()) {
      const destination = getIntendedDestination() || '/'
      navigate(destination, { replace: true })
    }
  }, [isAuthenticated, isTokenExpired, navigate])

  // Surface SSO error returned by the backend callback redirect (?sso_error=…)
  useEffect(() => {
    const ssoError = searchParams.get('sso_error')
    if (ssoError) setErrorMessage(`Single sign-on failed: ${ssoError}`)
  }, [searchParams])

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<LoginFormInput, unknown, LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  })

  const onSubmit = useCallback(
    async (data: LoginRequest) => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const response = await authApi.login(data)

        // Update auth store with user and tokens
        login(response.user, response.tokens)

        // Redirect to intended destination or launchpad
        const destination = getIntendedDestination() || '/'
        navigate(destination, { replace: true })
      } catch (error) {
        if (isApiError(error)) {
          setErrorMessage(error.message)
        } else if (error instanceof Error) {
          setErrorMessage(error.message)
        } else {
          setErrorMessage('An unexpected error occurred. Please try again.')
        }
      } finally {
        setIsLoading(false)
      }
    },
    [login, navigate]
  )

  const handleOAuthLogin = useCallback(async (provider: OAuthProvider) => {
    if (provider === 'okta') {
      // Server-driven redirect — let the browser hit /api/auth/sso/okta/login,
      // which will redirect to Okta and ultimately back to /oauth/callback.
      window.location.href = '/api/auth/sso/okta/login'
      return
    }
    console.warn(`OAuth login with ${provider} is not yet available`)
  }, [])

  const dismissError = useCallback(() => {
    setErrorMessage(null)
  }, [])

  // If authenticated, show nothing while redirecting
  if (isAuthenticated && !isTokenExpired()) {
    return null
  }

  return (
    <div className="container relative min-h-screen flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Left Panel - Branding (hidden on mobile) */}
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
        <div className="absolute inset-0 bg-zinc-900" />
        <GhostCursor color="#06b6d4" brightness={1.5} />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 h-6 w-6"
          >
            <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
          </svg>
          poli-x
        </div>
        <div className="relative z-20 mt-auto">
          <blockquote className="space-y-2">
            <p className="text-lg">
              &ldquo;poli-x gave us complete visibility and control over who accesses
              what data, across every database and AI agent in our stack. It&rsquo;s the
              single pane of glass we were missing.&rdquo;
            </p>
            <footer className="text-sm">
              <cite className="not-italic">Sofia Davis</cite>
              <span className="text-zinc-400"> - VP of Security</span>
            </footer>
          </blockquote>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="lg:p-8">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">
              Sign in to your account
            </h1>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access the PAP Console
            </p>
          </div>

          {/* Error Alert */}
          {errorMessage && (
            <Alert variant="destructive" className="relative">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="pr-8">{errorMessage}</AlertDescription>
              <button
                type="button"
                onClick={dismissError}
                className="absolute right-3 top-3 text-destructive hover:text-destructive/80"
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@company.com"
                autoComplete="email"
                disabled={isLoading}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className="min-h-[44px]"
                {...register('email')}
              />
              {errors.email && (
                <p id="email-error" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                  className="min-h-[44px] pr-10"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground min-w-[44px] min-h-[44px] flex items-center justify-center -mr-3"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p id="password-error" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center space-x-2 min-h-[44px]">
              <Checkbox id="rememberMe" {...register('rememberMe')} />
              <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
                Remember me
              </Label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full min-h-[44px]"
              disabled={isLoading || !isValid}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          {/* OAuth Buttons */}
          <div className="grid gap-3">
            {oauthProviders.map((provider) => {
              const enabled = provider.id === 'okta'
              return (
                <Button
                  key={provider.id}
                  variant="outline"
                  type="button"
                  className={cn(
                    'w-full min-h-[44px] justify-center',
                    provider.bgColor,
                    provider.textColor,
                    'border-0',
                    enabled ? '' : 'opacity-60 cursor-not-allowed'
                  )}
                  onClick={() => handleOAuthLogin(provider.id)}
                  disabled={!enabled || isLoading}
                  title={enabled ? `Sign in with ${provider.name}` : 'Coming soon'}
                >
                  {provider.icon}
                  <span className="ml-2">Continue with {provider.name}</span>
                </Button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
